/**
 * Versioned HTTP contract — `v1` — for the secure API gateway (`services/idea-engine-api`).
 *
 * WHY here (ADR 0023 §1, §5): the api service and the web app must parse the SAME
 * request/response shapes without importing one another (ADR 0001 — services never
 * share code except this package). So the wire contract lives here, expressed as Zod
 * schemas (the one place contracts carry a runtime parser), and BOTH sides
 * "parse, don't validate" against it (ADR 0005).
 *
 * Versioned by the `v1` namespace + the URL prefix (`/v1/generate`). Breaking
 * changes ship as `v2` alongside, never by mutating these shapes.
 *
 * Scope of this increment: the `generate` seam carrying a single stage's
 * LLM-supplied ASSUMPTIONS (the app still computes every RESULT). The real model
 * behind it is CONNECT-1; today the gateway answers with a schema-valid stub.
 */
import { z } from 'zod';
import { EFFORT_LEVELS, LLM_PROVIDERS } from '../llm-models.js';

/** Contract version string, surfaced in `meta` so clients can assert compatibility. */
export const HTTP_CONTRACT_VERSION = 'v1' as const;

// ── Envelope ─────────────────────────────────────────────────────────────────

/** A classified error as it crosses the wire (mirrors the service `AppError`). */
export const ApiErrorSchema = z.object({
  /** Coarse classification so clients can react without parsing the message. `forbidden`
   * is an auth deny (the api WAS reachable and rejected you) — distinct from `io`/`unknown`
   * (unreachable), so a client must NOT treat it as a transient outage to fall back from. */
  // Mirrors @squadlab/kernel ERROR_KINDS exactly (parity-tested; T16 closed).
  kind: z.enum(['cancelled', 'not_found', 'validation', 'io', 'forbidden', 'rate_limited', 'timeout', 'unknown']),
  /** Human-readable, non-sensitive message. Never carries secrets. */
  message: z.string(),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

/**
 * Per-call cost/usage, surfaced so the web app can show what an evaluation costs
 * (IDEA-14). Present only when a real model produced the response; absent in
 * simulation (the stub provider incurs no cost). `costUsd` is computed server-side
 * from a pinned price table — the browser never needs pricing or keys.
 */
export const ApiUsageSchema = z.object({
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  costUsd: z.number().nonnegative(),
  /** The model that actually served the call (tier routing may differ from the request). */
  model: z.string().min(1).max(120).optional(),
});
export type ApiUsage = z.infer<typeof ApiUsageSchema>;

/** Response metadata present on every envelope. */
export const ApiMetaSchema = z.object({
  /** Correlation id for tracing a request across services. */
  correlationId: z.string().min(1),
  /** Contract version that produced this response. */
  version: z.literal(HTTP_CONTRACT_VERSION),
  /** Token usage + USD cost for this call. Absent in simulation (no real spend). */
  usage: ApiUsageSchema.optional(),
});
export type ApiMeta = z.infer<typeof ApiMetaSchema>;

/**
 * The `{ data, error, meta }` envelope, parameterised over the success payload.
 * Exactly one of `data` / `error` is non-null. A factory (not a const) because the
 * payload schema varies per endpoint.
 */
export function apiEnvelope<T extends z.ZodTypeAny>(data: T) {
  return z.object({
    data: data.nullable(),
    error: ApiErrorSchema.nullable(),
    meta: ApiMetaSchema,
  });
}

// ── Per-stage assumptions (the shared wire shape) ────────────────────────────

const num = z.number().finite();
const flag = z.boolean();
/** A QSAM band level: 0–4 (4 = best). The engine maps it to a rate via the scoring matrix. */
const level = z.number().finite(); // 0–4 by intent; the engine clamps at its boundary

/** Ordered stage keys mirrored from the engine framework (kept in lockstep). */
export const STAGE_KEYS = [
  'tam',
  'sam',
  'qsam',
  'aom',
  'ivs',
  'ram',
  'customerValue',
  'unitEconomics',
  'cacLtv',
  'winRate',
  'defensibility',
  'scenarios',
  'confidence',
] as const;
export const StageKeySchema = z.enum(STAGE_KEYS);
export type StageKeyV1 = z.infer<typeof StageKeySchema>;

/**
 * Generation tier for a model call (IDEA-35 / ADR 0047, ADR 0048). A **server-controlled
 * profile selector** — NOT a raw model id — so an untrusted browser can never pick an
 * arbitrary (expensive) model through the wire: the gateway maps each tier to a model it
 * chose (`LLM_MODEL` for `interactive`, `LLM_BACKGROUND_MODEL` for `background`), both pinned
 * to the DEVOP-8 allowlist. `background` routes the async worker's calls to the latest Sonnet
 * (same price tier as the interactive Sonnet default — no cost escalation, never Opus).
 * Absent ⇒ `interactive` (the browser default), so the interactive path is unchanged.
 */
export const GENERATION_TIERS = ['interactive', 'background'] as const;
export const GenerationTierSchema = z.enum(GENERATION_TIERS);
export type GenerationTier = z.infer<typeof GenerationTierSchema>;

/**
 * Explicit operator model choice (IDEA-48 / ADR 0054) — the ONE sanctioned exception to
 * the tier-only rule above. Optional on every request that carries it: absent keeps tier
 * routing byte-for-byte. The gateway validates a present selection FAIL-CLOSED against
 * its own catalog (unknown provider/model, unsupported thinking level → 400 `validation`)
 * — this schema names the shape, the server catalog names the universe. `thinking.level`
 * maps to Anthropic adaptive thinking + `output_config.effort` (never `budget_tokens`).
 */
export const EffortLevelSchema = z.enum(EFFORT_LEVELS);
export const ModelSelectionSchema = z.object({
  provider: z.enum(LLM_PROVIDERS),
  model: z.string().min(1).max(120),
  thinking: z.object({ level: EffortLevelSchema }).optional(),
});
export type ModelSelectionV1 = z.infer<typeof ModelSelectionSchema>;

/**
 * Per-stage assumption schemas — the wire shape the gateway returns and the web
 * app parses. Field shapes mirror the engine's `*Assumptions` contracts; the
 * engine re-parses/clamps at its own boundary, so this stays a transport schema.
 */
export const StageAssumptionsSchema = {
  tam: z.object({ totalCustomers: num, annualRevenuePerCustomer: num }),
  sam: z.object({ serviceableCustomers: num }),
  qsam: z.object({
    painLevel: level,
    budgetFitLevel: level,
    dataAvailabilityLevel: level,
    legalEligibilityLevel: level,
    digitalReadinessLevel: level,
    /**
     * Fused single-pass QSAM (IDEA-37): the per-filter REASONING authored in the SAME model call
     * that chose the bands, keyed by metric (`pain`/`budgetFit`/…). Optional (older/mock payloads
     * omit it). The engine lifts it into the QSAM insight and strips it before storing the bands,
     * so the band and its reasoning can never drift — they come from one pass.
     */
    reasoning: z.record(z.string()).optional(),
  }),
  aom: z.object({
    awarenessLevel: level,
    urgencyLevel: level,
    trustLevel: level,
    adoptionEaseLevel: level,
    competitiveWinLevel: level,
    retentionLevel: level,
    /** Fused single-pass AOM (IDEA-37): per-gate reasoning authored WITH the bands; see qsam.reasoning. */
    reasoning: z.record(z.string()).optional(),
  }),
  ivs: z.object({
    problemRecognition: num,
    outcomeClarity: num,
    explanationSimplicity: num,
    behaviorFit: num,
    urgencyDesire: num,
    trustSimplicity: num,
  }),
  ram: z.object({
    technicalLevel: level,
    regulatoryLevel: level,
    dataRightsLevel: level,
    securityLevel: level,
    monetizationLevel: level,
    distributionLevel: level,
    /** Fused single-pass RAM (IDEA-37): per-gate reasoning authored WITH the bands; see qsam.reasoning. */
    reasoning: z.record(z.string()).optional(),
  }),
  customerValue: z.object({
    costSavings: num,
    revenueIncrease: num,
    riskReduction: num,
    timeSavings: num,
    switchingCost: num,
    integrationCost: num,
    newOperatingCost: num,
    annualPrice: num,
  }),
  unitEconomics: z.object({
    aiComputeCost: num,
    apiCost: num,
    dataCost: num,
    cloudCost: num,
    supportCost: num,
    humanReviewCost: num,
  }),
  cacLtv: z.object({ acquisitionSpend: num, newCustomers: num, customerLifetimeYears: num }),
  winRate: z.object({
    productLevel: level,
    priceLevel: level,
    distributionLevel: level,
    trustLevel: level,
    switchingLevel: level,
    /** Fused single-pass win rate (IDEA-37): per-front reasoning authored WITH the bands; see qsam.reasoning. */
    reasoning: z.record(z.string()).optional(),
  }),
  defensibility: z.object({
    dataAdvantage: num,
    workflowLockin: num,
    switchingCost: num,
    distributionControl: num,
    brandTrust: num,
    networkEffects: num,
    regulatoryAdvantage: num,
  }),
  scenarios: z.object({
    conservativeMultiplier: num.optional(),
    aggressiveMultiplier: num.optional(),
  }),
  confidence: z.object({
    marketQualityScore: num,
    problemPainScore: num,
    adoptionScore: num,
    distributionScore: num,
    noClearBuyer: flag,
    noPainfulProblem: flag,
    accuracyRequirementTooHigh: flag,
    regulatoryBlocks: flag,
  }),
} as const satisfies Record<StageKeyV1, z.ZodTypeAny>;

/** Any single stage's assumptions — the union of all per-stage shapes. */
export const AnyStageAssumptionsSchema = z.union([
  StageAssumptionsSchema.tam,
  StageAssumptionsSchema.sam,
  StageAssumptionsSchema.qsam,
  StageAssumptionsSchema.aom,
  StageAssumptionsSchema.ivs,
  StageAssumptionsSchema.ram,
  StageAssumptionsSchema.customerValue,
  StageAssumptionsSchema.unitEconomics,
  StageAssumptionsSchema.cacLtv,
  StageAssumptionsSchema.winRate,
  StageAssumptionsSchema.defensibility,
  StageAssumptionsSchema.scenarios,
  StageAssumptionsSchema.confidence,
]);
export type AnyStageAssumptions = z.infer<typeof AnyStageAssumptionsSchema>;

// ── POST /v1/generate ────────────────────────────────────────────────────────

/**
 * Request body for `POST /v1/generate`: produce one stage's assumptions for an
 * idea. `brief` is optional context for the (future) real model; the stub ignores
 * it. Bounded sizes guard against oversized payloads (DEVOP-8 hardens further).
 */
export const GenerateRequestSchema = z.object({
  ideaId: z.string().min(1).max(64),
  stageKey: StageKeySchema,
  brief: z.string().max(2000).optional(),
  /** Server-mapped model profile (IDEA-35). Absent ⇒ `interactive`; the async worker sends
   *  `background` to route this call to the latest Sonnet. See {@link GenerationTierSchema}. */
  tier: GenerationTierSchema.optional(),
  /** Explicit operator model/thinking choice (IDEA-48) — overrides tier routing when present. */
  modelSelection: ModelSelectionSchema.optional(),
  /** Run-wide operator steer (IDEA-51): natural-language guidance broadcast to every stage
   *  call with relevance framing ("apply only what is relevant to THIS stage"). */
  steer: z.string().min(1).max(2000).optional(),
});
export type GenerateRequest = z.infer<typeof GenerateRequestSchema>;

/**
 * Success payload for `POST /v1/generate`: the requested stage's assumptions.
 *
 * `assumptions` is parsed against the schema for THIS `stageKey` specifically —
 * NOT a permissive union. (A union over the per-stage shapes is unsafe here: the
 * all-optional `scenarios` shape matches any object and would silently strip a
 * `confidence`/`tam` payload to `{}`.) So we take `assumptions` as raw and re-parse
 * it by `stageKey`, replacing it with the typed, validated result.
 */
export const GenerateDataSchema = z
  .object({
    ideaId: z.string().min(1),
    stageKey: StageKeySchema,
    assumptions: z.unknown(),
  })
  .transform((value, ctx) => {
    const stageResult = StageAssumptionsSchema[value.stageKey].safeParse(value.assumptions);
    if (!stageResult.success) {
      for (const issue of stageResult.error.issues) {
        ctx.addIssue({ ...issue, path: ['assumptions', ...issue.path] });
      }
      return z.NEVER;
    }
    return { ideaId: value.ideaId, stageKey: value.stageKey, assumptions: stageResult.data as AnyStageAssumptions };
  });
export type GenerateData = z.infer<typeof GenerateDataSchema>;

/** Full `POST /v1/generate` response envelope. */
export const GenerateResponseSchema = apiEnvelope(GenerateDataSchema);
export type GenerateResponse = z.infer<typeof GenerateResponseSchema>;

// ── POST /v1/provenance (CONNECT-2 — real market data) ───────────────────────

/**
 * How a sourced number was obtained. Mirrors the engine's `SourceMethod` so the
 * web app can map a gateway record onto its existing `Provenance` shape without a
 * second vocabulary (ADR 0017).
 */
export const SOURCE_METHODS = ['measured', 'benchmark', 'modeled', 'estimated', 'llm-research', 'proxy'] as const;
export const SourceMethodSchema = z.enum(SOURCE_METHODS);
export type SourceMethodV1 = z.infer<typeof SourceMethodSchema>;

/** Coarse trust band for a sourced value. */
export const CONFIDENCE_LEVELS = ['low', 'med', 'high'] as const;
export const ConfidenceSchema = z.enum(CONFIDENCE_LEVELS);
export type ConfidenceV1 = z.infer<typeof ConfidenceSchema>;

/** One supporting evidence pointer carried with a provenance record. */
export const ProvenanceEvidenceSchema = z.object({
  label: z.string().min(1).max(200),
  detail: z.string().max(500).optional(),
  url: z.string().url().max(500).optional(),
});
export type ProvenanceEvidenceV1 = z.infer<typeof ProvenanceEvidenceSchema>;

/**
 * Request body for `POST /v1/provenance` (CONNECT-2): "where did this number come
 * from?". Given a stage/field and the resolved value, the gateway returns a
 * provenance record from a real data-source connector (the stub returns a
 * deterministic record while egress/keys are deploy-only). `value` is a number or
 * boolean (the two datum shapes the console renders).
 */
export const ProvenanceRequestSchema = z.object({
  stageKey: StageKeySchema,
  field: z.string().min(1).max(120),
  value: z.union([z.number().finite(), z.boolean()]),
});
export type ProvenanceRequest = z.infer<typeof ProvenanceRequestSchema>;

/**
 * Success payload for `POST /v1/provenance`: a single provenance record. Mirrors
 * the engine's `Provenance` so the web adapter maps it straight onto the existing
 * port (no view change). `asOf` is an ISO timestamp set by the gateway's clock.
 */
export const ProvenanceDataSchema = z.object({
  sourceId: z.string().min(1).max(120),
  sourceLabel: z.string().min(1).max(200),
  method: SourceMethodSchema,
  confidence: ConfidenceSchema,
  asOf: z.string().min(1),
  evidence: z.array(ProvenanceEvidenceSchema),
  note: z.string().max(500).optional(),
});
export type ProvenanceData = z.infer<typeof ProvenanceDataSchema>;

/** Full `POST /v1/provenance` response envelope. */
export const ProvenanceResponseSchema = apiEnvelope(ProvenanceDataSchema);
export type ProvenanceResponse = z.infer<typeof ProvenanceResponseSchema>;

// ── POST /v1/evidence (CONNECT-3 — real evidence) ────────────────────────────

/**
 * Request body for `POST /v1/evidence` (CONNECT-3): resolve supporting citations for
 * ONE datum. Carries the datum's identity (stage/field/label/value) plus its
 * provenance (from CONNECT-2) so the research pipeline can target the right sources.
 * The gateway runs a search→fetch→extract→cite pipeline (the stub returns
 * deterministic, dated, attributed citations while egress/keys are deploy-only).
 */
export const EvidenceRequestSchema = z.object({
  stageKey: StageKeySchema,
  field: z.string().min(1).max(120),
  label: z.string().min(1).max(200),
  value: z.union([z.number().finite(), z.boolean()]),
  provenance: ProvenanceDataSchema,
});
export type EvidenceRequest = z.infer<typeof EvidenceRequestSchema>;

/** One resolved citation. Mirrors the engine's `EvidenceItem` (no view change). */
export const EvidenceItemSchema = z.object({
  title: z.string().min(1).max(300),
  snippet: z.string().min(1).max(1000),
  /** Human-readable source attribution, e.g. "Statista · 2025". */
  source: z.string().min(1).max(200),
  /** Citation URL. */
  url: z.string().url().max(500),
  /** ISO timestamp the citation was retrieved. */
  retrievedAt: z.string().min(1),
  /** How it was retrieved, e.g. "semantic search". */
  method: z.string().min(1).max(120),
  /** Relevance to the queried value, [0,1]. */
  relevance: z.number().min(0).max(1),
});
export type EvidenceItemV1 = z.infer<typeof EvidenceItemSchema>;

/**
 * Success payload for `POST /v1/evidence`: resolved citations for one datum. Mirrors
 * the engine's `ResolvedEvidence` so the web adapter maps it straight onto the
 * existing port. `simulated` is true while the stub answered (drives the SIMULATION
 * hint); a real pipeline returns false.
 */
export const EvidenceDataSchema = z.object({
  summary: z.string().min(1).max(500),
  items: z.array(EvidenceItemSchema),
  simulated: z.boolean(),
});
export type EvidenceData = z.infer<typeof EvidenceDataSchema>;

/** Full `POST /v1/evidence` response envelope. */
export const EvidenceResponseSchema = apiEnvelope(EvidenceDataSchema);
export type EvidenceResponse = z.infer<typeof EvidenceResponseSchema>;

// ── POST /v1/ideate (CONNECT-1 — model-proposed new ideas) ───────────────────

/**
 * Request body for `POST /v1/ideate` (CONNECT-1): ask the model to PROPOSE a
 * brand-new venture idea. All fields are optional — an empty body is a valid
 * "surprise me" request (autonomous generation). `guidelines` carries the
 * operator's freeform steer: a plain-language directive that can be a segment ("b2b
 * idea"), an audience ("b2c targeting child education"), or a full multi-paragraph
 * brief. `avoidTitles` lets the client pass recent titles so the model doesn't repeat
 * itself. Bounded sizes guard against oversized payloads.
 *
 * The gateway returns ONLY a title + brief (the creative part). The web app mints
 * the id/owner and registers a stand-in assumption profile locally so the new idea
 * is immediately evaluable — the engine still computes every result (mirrors the
 * existing operator-intake `materialize` seam). A v2 could add model-proposed
 * assumptions; keeping them out here keeps this contract minimal (one-way door).
 */
/**
 * Max length of the ideate `guidelines` steer, in characters. Deliberately generous so an
 * operator can paste a long, detailed brief rather than a one-liner: ~40k chars is roughly
 * 10k tokens (English averages ~4 chars/token), which is only ~5% of the model's 200k-token
 * context once the ~1k-token system prompt and the avoid-list are added — so a full-length
 * steer always fits the ideate prompt with room to spare. The client textarea, the client
 * adapter clamp, and this wire schema ALL derive from this one constant, so the cap can never
 * drift between them (the old hand-copied `500` lived in three places).
 */
export const IDEATE_GUIDELINES_MAX_CHARS = 40_000;

export const IdeateRequestSchema = z.object({
  guidelines: z.string().max(IDEATE_GUIDELINES_MAX_CHARS).optional(),
  avoidTitles: z.array(z.string().min(1).max(200)).max(50).optional(),
  /** Explicit operator model/thinking choice (IDEA-48) — overrides tier routing when present. */
  modelSelection: ModelSelectionSchema.optional(),
});
export type IdeateRequest = z.infer<typeof IdeateRequestSchema>;

/**
 * Success payload for `POST /v1/ideate`: a proposed idea's title + brief. Sizes
 * mirror the {@link import('../idea.js').Idea} domain shape so the web app maps it
 * straight onto a new idea (no view change).
 */
/**
 * CLAMP (not reject) a model-proposed string to its wire cap (ADR 0050). The latest Sonnet
 * intermittently emits a `title`/`brief` past the cap (the over-length quirk that 502'd spec/analyze
 * too), and a strict parse would fail the WHOLE proposal — surfacing as "Could not propose an idea —
 * io: … too_big … brief". Clamping keeps the proposal usable; the web draft boundary
 * (`idea-draft-validation.ts`) applies the authoritative checks. Keeps `min(1)` so a genuinely empty
 * proposal still fails loudly rather than banking a blank idea.
 */
const proposalStr = (max: number) =>
  z.preprocess((v) => (typeof v === 'string' ? v.trim().slice(0, max) : v), z.string().min(1).max(max));

export const IdeateDataSchema = z.object({
  title: proposalStr(200),
  brief: proposalStr(2000),
});
export type IdeateData = z.infer<typeof IdeateDataSchema>;

/** Full `POST /v1/ideate` response envelope. */
export const IdeateResponseSchema = apiEnvelope(IdeateDataSchema);
export type IdeateResponse = z.infer<typeof IdeateResponseSchema>;

// ── POST /v1/refine (CONNECT-1 — model-structured operator intake) ───────────

/**
 * Request body for `POST /v1/refine` (CONNECT-1, backlog N13): turn an operator's
 * free-text idea into a structured, reviewable draft. Unlike `/v1/ideate` (which
 * INVENTS an idea), refine TRANSFORMS the operator's own words — so `description` is
 * required. `title` is an optional operator-supplied name the model should honour
 * rather than derive. Bounded sizes guard against oversized payloads (DEVOP-8).
 *
 * The gateway returns a `{ title, brief }` draft; the web app re-parses it through
 * the SAME draft edge layer the mock refine uses (`idea-draft-validation.ts`), so the
 * live and simulated intake paths converge on one typed draft (parse-don't-validate).
 */
export const RefineRequestSchema = z.object({
  description: z.string().min(1).max(2000),
  title: z.string().max(200).optional(),
  /** Explicit operator model/thinking choice (IDEA-54) — the steered meta refresh honours it. */
  modelSelection: ModelSelectionSchema.optional(),
  /**
   * Reposition mode (IDEA-54): when set, `description`+`title` are an EXISTING venture and
   * this is the operator's regeneration steer, applied as a *conservative, relevance-gated*
   * adjustment — the model rewrites the title/brief only where the steer concerns the
   * venture's identity, and otherwise returns them essentially unchanged. It must NEVER
   * replace the venture with the steer text itself (the bug this field fixes). Absent for a
   * normal intake refine, whose title is honoured verbatim.
   */
  reposition: z.string().min(1).max(2000).optional(),
});
export type RefineRequest = z.infer<typeof RefineRequestSchema>;

/**
 * Success payload for `POST /v1/refine`: a structured idea draft (title + brief).
 * Same shape as {@link IdeateDataSchema}; the web app clips to its tighter draft
 * caps before banking, so these generous bounds only guard the wire.
 */
export const RefineDataSchema = z.object({
  title: proposalStr(200),
  brief: proposalStr(2000),
});
export type RefineData = z.infer<typeof RefineDataSchema>;

/** Full `POST /v1/refine` response envelope. */
export const RefineResponseSchema = apiEnvelope(RefineDataSchema);
export type RefineResponse = z.infer<typeof RefineResponseSchema>;

// ── POST /v1/spec (CONNECT-1 — model-authored build specification) ────────────

/**
 * Request body for `POST /v1/spec` (IDEA-7 live path): produce the software build
 * specification for an EVALUATED idea. Carries the idea's identity + brief and a compact
 * verdict summary so the model can ground the spec in the evaluation (bottleneck/risk feed
 * the risks section; `grossMargin` informs scope). The gateway returns the spec BODY only;
 * the web app stamps `ideaId`/`schemaVersion`/`simulated`/`generatedAt` and re-parses it
 * through the same `specification-validation` boundary the mock uses (ADR 0005).
 */
export const SpecRequestSchema = z.object({
  ideaId: z.string().min(1).max(64),
  title: z.string().min(1).max(200),
  brief: z.string().min(1).max(4000),
  verdict: z
    .object({
      band: z.string().min(1).max(40),
      score: z.number().finite(),
      bottleneck: z.string().max(500).optional(),
      risk: z.string().max(500).optional(),
    })
    .optional(),
  grossMargin: z.number().finite().optional(),
  /** Server-mapped model profile (IDEA-35). Absent ⇒ `interactive`; the async worker sends
   *  `background` to route the spec call to the latest Sonnet. See {@link GenerationTierSchema}. */
  tier: GenerationTierSchema.optional(),
  /** Explicit operator model/thinking choice (IDEA-48) — overrides tier routing when present. */
  modelSelection: ModelSelectionSchema.optional(),
});
export type SpecRequest = z.infer<typeof SpecRequestSchema>;

/**
 * NEVER-THROW normalisation at the LLM boundary (ADR 0050). The latest Sonnet intermittently
 * violates the forced-tool schema every possible way — a field past its cap (`too_big`), an
 * array collapsed into a string, an empty string (`too_small`), an omitted field
 * (`invalid_type: undefined`), too many items — and a strict parse 502'd the WHOLE call over any
 * one of them (whack-a-mole; strict tool use fixed structure but ~doubled latency). These bounds
 * are wire guards, not semantics (the web `specification-validation` boundary does the
 * authoritative non-empty checks), so the gateway returns a **best-effort normalised** object and
 * NEVER rejects: over-long → clamped, collapsed string → split, missing/wrong → default, a bad
 * array element → dropped (the good ones survive).
 */
/** A bare open/close section tag Sonnet sometimes emits as its own array entry
 *  (`<scopeIn>`, `</users>`) when it collapses the object into XML-tagged lines. */
const BARE_TAG = /^<\/?[a-zA-Z][\w-]*>$/;
/** A single value wrapped in a same-name open/close tag (`<value>text</value>`). */
const XML_WRAP = /^<([a-zA-Z][\w-]*)>([\s\S]*)<\/\1>$/;
/**
 * Strip the XML-ish wrappers Sonnet leaks when it degrades a forced-tool call into a flat
 * tagged dump (ADR 0050): a `<value>…</value>` wrapper → its inner text; a bare section tag
 * (`<scopeIn>`) → `''` (dropped downstream). Cosmetic cleanup only — a full collapse is
 * prevented by the prompt + the degenerate-retry, not repaired here.
 */
const stripTags = (s: string): string => {
  const t = s.trim();
  if (BARE_TAG.test(t)) return '';
  const m = XML_WRAP.exec(t);
  return (m ? m[2]! : t).trim();
};

const clampStr = (max: number) =>
  z.preprocess((v) => (typeof v === 'string' ? stripTags(v).slice(0, max) : v), z.string().max(max)).catch('');
const specName = clampStr(300);
const specLine = clampStr(1200);

/** Tolerant array-of-lines: a real array OR a string Sonnet collapsed it into (split on newlines,
 *  stripping `- `/`* `/`1. ` list markers + any leaked XML wrappers), empties dropped, count clamped,
 *  each element clamped by its own schema; never throws. */
const linesOf = (item: z.ZodTypeAny, max: number) =>
  z.preprocess((v) => {
    if (typeof v === 'string') {
      return v.split(/\r?\n/).map((s) => stripTags(s.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, ''))).filter(Boolean).slice(0, max);
    }
    if (Array.isArray(v)) {
      return v.map((s) => (typeof s === 'string' ? stripTags(s) : s)).filter((s) => s !== '' && s != null).slice(0, max);
    }
    return [];
  }, z.array(item).max(max)).catch([] as string[]);

/** Object-array that degrades PER ELEMENT: a malformed object is dropped, the good ones survive
 *  (rather than one bad element voiding the whole array). Count clamped; never throws. */
const objArray = <T extends z.ZodTypeAny>(item: T, max: number) =>
  z.preprocess((v) => (Array.isArray(v) ? v.slice(0, max) : []), z.array(z.unknown())).transform((arr) =>
    arr.flatMap((el) => {
      const r = item.safeParse(el);
      return r.success ? [r.data as z.infer<T>] : [];
    }),
  );

/**
 * Success payload for `POST /v1/spec`: the specification BODY (mirrors the engine's
 * {@link import('../specification.js').Specification} minus the envelope fields the web app
 * stamps — `ideaId`/`schemaVersion`/`simulated`/`generatedAt`). The gateway normalises the model
 * output to these bounds and never rejects; the web boundary applies the authoritative checks.
 */
export const SpecDataSchema = z.object({
  title: specName,
  summary: clampStr(2400),
  problem: clampStr(2400),
  users: linesOf(specLine, 12),
  scopeIn: linesOf(specLine, 20),
  scopeOut: linesOf(specLine, 20),
  flows: objArray(z.object({ name: specName, steps: linesOf(specLine, 12) }), 12),
  architecture: objArray(z.object({ name: specName, responsibility: specLine }), 20),
  dataModel: objArray(z.object({ name: specName, fields: linesOf(specLine, 20) }), 20),
  integrations: linesOf(specLine, 20),
  milestones: objArray(z.object({ name: specName, outcome: specLine }), 12),
  risks: objArray(z.object({ risk: specLine, mitigation: specLine }), 12),
});
export type SpecData = z.infer<typeof SpecDataSchema>;

/** Full `POST /v1/spec` response envelope. */
export const SpecResponseSchema = apiEnvelope(SpecDataSchema);
export type SpecResponse = z.infer<typeof SpecResponseSchema>;

// ── POST /v1/explain (IDEA-34 — model-authored plain-language stage Q&A) ──────

const derivLabel = z.string().min(1).max(160);
const derivValue = z.string().min(1).max(160);
const derivRow = z.object({ label: derivLabel, value: derivValue });

/**
 * Request body for `POST /v1/explain` (IDEA-34): author a plain-language Q&A explaining how
 * ONE stage's score was reached. Carries the stage's already-computed deterministic
 * DERIVATION (inputs × formula = result) + a compact verdict summary — the **grounding
 * invariant** (ADR 0035): the gateway never re-runs engine math, it narrates these supplied
 * numbers. The gateway returns the Q&A BODY only; the web app stamps `simulated`/`generatedAt`
 * and re-parses through `explanation-validation` (ADR 0005).
 */
export const ExplainRequestSchema = z.object({
  ideaId: z.string().min(1).max(64),
  stageKey: z.string().min(1).max(40),
  title: z.string().min(1).max(200),
  brief: z.string().min(1).max(4000),
  derivation: z.object({
    title: derivLabel,
    inputs: z.array(derivRow).max(24),
    formula: z.string().min(1).max(600),
    steps: z.array(derivRow).max(24).optional(),
    result: derivRow,
  }),
  verdict: z
    .object({
      band: z.string().min(1).max(40),
      score: z.number().finite(),
      upside: z.string().max(500).optional(),
      bottleneck: z.string().max(500).optional(),
    })
    .optional(),
});
export type ExplainRequest = z.infer<typeof ExplainRequestSchema>;

/**
 * Success payload for `POST /v1/explain`: 1–6 ordered question→answer pairs (the BODY; the
 * web app stamps `simulated`/`generatedAt`). Bounds guard the wire; the web boundary applies
 * the authoritative checks.
 */
export const ExplainDataSchema = z.object({
  qa: z
    .array(z.object({ question: z.string().min(1).max(400), answer: z.string().min(1).max(2000) }))
    .min(1)
    .max(6),
});
export type ExplainData = z.infer<typeof ExplainDataSchema>;

/** Full `POST /v1/explain` response envelope. */
export const ExplainResponseSchema = apiEnvelope(ExplainDataSchema);
export type ExplainResponse = z.infer<typeof ExplainResponseSchema>;

// ── POST /v1/analyze (IDEA-36 — per-sub-metric analysis that yields each score) ──

/**
 * Request body for `POST /v1/analyze` (IDEA-36): author the per-sub-metric qualitative
 * analysis for ONE stage, in one shot, anchored to the stage's computed numbers. Carries the
 * stage's assumptions (field → value) + its computed results so the model narrates real figures
 * (CAC line items must sum to the computed CAC, competitor factors match the scores, …). The
 * gateway returns the analysis BODY; the web app stamps `generatedAt` + reuses the returned
 * `modelId` for lineage, and re-parses through `core/insight-validation.ts` (ADR 0005).
 */
export const AnalyzeRequestSchema = z.object({
  ideaId: z.string().min(1).max(64),
  stageKey: StageKeySchema,
  title: z.string().min(1).max(200),
  brief: z.string().min(1).max(4000),
  /** The stage's assumption fields (field → value) — structural; varies per stage. */
  assumptions: z.record(z.unknown()),
  /** The stage's computed results (cac, ltv, baseAom, …) — structural; varies per stage. */
  results: z.record(z.unknown()),
  verdict: z.object({ band: z.string().min(1).max(40), score: z.number().finite() }).optional(),
  /** Server-mapped model profile (IDEA-35). Absent ⇒ `interactive`; the async worker sends
   *  `background` to route the analysis call to the latest Sonnet. See {@link GenerationTierSchema}. */
  tier: GenerationTierSchema.optional(),
  /** Explicit operator model/thinking choice (IDEA-48) — overrides tier routing when present. */
  modelSelection: ModelSelectionSchema.optional(),
  /** Run-wide operator steer (IDEA-51) — same relevance-framed broadcast as generate. */
  steer: z.string().min(1).max(2000).optional(),
});
export type AnalyzeRequest = z.infer<typeof AnalyzeRequestSchema>;

// NEVER-THROW normalisation for the analysis too (ADR 0050) — same Sonnet schema-adherence quirk
// as the spec: clamp over-long strings, coerce a collapsed array-string, drop bad elements, default
// a missing field. The web `core/insight-validation.ts` boundary does the authoritative checks.
const analyzeText = clampStr(16000);
const analyzeShort = clampStr(600);
const analyzeNote = clampStr(800).optional();
const analyzeFactor = z.object({
  factor: analyzeShort,
  you: z.number().finite(),
  them: z.number().finite(),
  note: analyzeNote,
});

/**
 * Success payload for `POST /v1/analyze`: the analysis BODY. `fields` is an array (concrete
 * tool schema) the web maps to the `perField` record; the rest are the stage extras. `modelId`
 * is the source model (lineage). Generous text limits — longer analysis improves the reasoning.
 */
export const AnalyzeDataSchema = z.object({
  fields: objArray(z.object({ field: analyzeShort, analysis: analyzeText, drivers: linesOf(analyzeShort, 8).optional() }), 24),
  competitors: objArray(z.object({ name: analyzeShort, factors: objArray(analyzeFactor, 12), note: analyzeNote }), 8).optional(),
  cacLineItems: objArray(z.object({ label: analyzeShort, amount: z.number().finite(), note: analyzeNote }), 12).optional(),
  ltvMath: clampStr(4000).optional(),
  scenarioGrowth: clampStr(8000).optional(),
  demographics: z.object({ segment: analyzeShort, buyer: analyzeShort, geography: analyzeShort, tech: analyzeShort }).optional(),
  summary: clampStr(4000).optional(),
  /** The model that authored this analysis (lineage), e.g. `claude-opus-4-8` or `stub`. */
  modelId: analyzeShort,
});
export type AnalyzeData = z.infer<typeof AnalyzeDataSchema>;

/** Full `POST /v1/analyze` response envelope. */
export const AnalyzeResponseSchema = apiEnvelope(AnalyzeDataSchema);
export type AnalyzeResponse = z.infer<typeof AnalyzeResponseSchema>;

// ── POST /v1/need/analyze (IDEA-62 — "Analyse with AI" over the real-need checklist) ──

/**
 * Request body for `POST /v1/need/analyze` (IDEA-62): judge each real-need consideration for
 * one idea via the model. The considerations' *catalog* (keys, questions, prompts) lives in the
 * engine — the contract just carries the items to judge, so the taxonomy can evolve without a
 * schema change. The gateway returns one `{verdict, reasoning}` per key + the model that answered;
 * the web app merges them into `needAssessment` (preserving human verdicts) and persists.
 */
export const NeedAnalyzeRequestSchema = z.object({
  ideaId: z.string().min(1).max(64),
  title: z.string().min(1).max(200),
  brief: z.string().min(1).max(4000),
  /** The considerations to judge — `key` addresses the verdict; `question`/`aiPrompt` guide the model. */
  considerations: z
    .array(
      z.object({
        key: z.string().min(1).max(60),
        question: z.string().min(1).max(400),
        aiPrompt: z.string().min(1).max(600),
      }),
    )
    .min(1)
    .max(24),
  /** Explicit operator model/thinking choice (IDEA-48) — overrides tier routing when present. */
  modelSelection: ModelSelectionSchema.optional(),
  /** Server-mapped model profile (IDEA-35). Absent ⇒ `interactive`; the async worker sends `background`. */
  tier: GenerationTierSchema.optional(),
  /** Run-wide operator steer (IDEA-51) — same relevance-framed broadcast as generate/analyze. */
  steer: z.string().min(1).max(2000).optional(),
});
export type NeedAnalyzeRequest = z.infer<typeof NeedAnalyzeRequestSchema>;

/** One consideration's AI verdict — the judgment plus the reasoning behind it. NEVER-THROW clamps. */
const needVerdictItem = z.object({
  key: clampStr(60),
  verdict: z.enum(['yes', 'maybe', 'no']).catch('maybe'),
  reasoning: clampStr(2000),
});

/**
 * Success payload for `POST /v1/need/analyze`: one `{key, verdict, reasoning}` per judged
 * consideration + the model that authored them (lineage). A per-element-tolerant array (a
 * malformed judgment is dropped, the good ones survive); the web maps it to the keyed record.
 */
export const NeedAnalyzeDataSchema = z.object({
  considerations: objArray(needVerdictItem, 24),
  /** The model that authored the judgments (lineage), e.g. `claude-sonnet-5` or `stub`. */
  modelId: clampStr(120),
});
export type NeedAnalyzeData = z.infer<typeof NeedAnalyzeDataSchema>;

/** Full `POST /v1/need/analyze` response envelope. */
export const NeedAnalyzeResponseSchema = apiEnvelope(NeedAnalyzeDataSchema);
export type NeedAnalyzeResponse = z.infer<typeof NeedAnalyzeResponseSchema>;

// ── GET /health ──────────────────────────────────────────────────────────────

/**
 * Persistence backend the api resolved at startup (DEVOP-12): `supabase` when the
 * `SUPABASE_*` creds are present (durable, hosted), else `memory` (the in-memory stub —
 * NOT durable; data is lost when the isolate recycles). Surfaced so the app can show
 * the operator, with certainty, whether their work is being saved durably.
 */
export const StorageBackendSchema = z.enum(['supabase', 'memory']);
export type StorageBackend = z.infer<typeof StorageBackendSchema>;

/**
 * LLM backend the api resolved at startup (IDEA-11): `live` when a real model
 * provider is configured (an `ANTHROPIC_API_KEY` is present server-side), else
 * `stub` (the deterministic simulation provider — no model call, no cost).
 * Surfaced so the app can show, with certainty, whether the AI is truly plugged
 * in — without waiting for an evaluation to emit an `llm.cost` event.
 */
export const LlmBackendSchema = z.enum(['live', 'stub']);
export type LlmBackend = z.infer<typeof LlmBackendSchema>;

/**
 * In-Worker Cloudflare Access guard state (DEVOP-11): `enforced` (validates the Access
 * JWT on `/v1/*`), `disabled` (explicit opt-out — trusts edge Zero Trust, serves without
 * the in-Worker check), or `unconfigured` (no ACCESS_* and not disabled → fail-closed,
 * denies every `/v1/*`). Surfaced so the app can show the operator the live auth posture.
 */
export const AccessModeSchema = z.enum(['enforced', 'disabled', 'unconfigured']);
export type AccessMode = z.infer<typeof AccessModeSchema>;

/** Success payload for `GET /health`. */
export const HealthDataSchema = z.object({
  status: z.literal('ok'),
  storage: StorageBackendSchema,
  llm: LlmBackendSchema,
  access: AccessModeSchema,
  /**
   * Build identity of the **deployed api** — the git commit SHA this Function was built
   * from (`CF_PAGES_COMMIT_SHA`), or `'unknown'` when not deployed via the pipeline.
   * Surfaced so an operator can confirm, with certainty, *which* version is live and
   * compare it to the web bundle's build SHA (shown on `/diagnostics`) and to `main` —
   * answering "am I actually running the latest deploy, or a stale alias/cache?".
   */
  version: z.string(),
  /**
   * The api's resolved **runtime feature decisions**, as a `name → state` map (e.g.
   * `{ llm: 'live', data: 'stub', storage: 'supabase', access: 'enforced' }`). This is the
   * single, generic surface for the Functions-runtime flags so `/diagnostics` can list
   * them ALL by iteration — a new flag added to the map automatically appears, no UI
   * change. Distinct from the WEB build flags (`config.flags`), which the app injects on
   * its own side; together they show the full live wiring. Optional so an older api that
   * predates this field still parses (the app reads an empty map).
   */
  flags: z.record(z.string()).optional(),
  /**
   * Evidence-engine backend liveness (IDEA-55) — which adapter the RUNTIME resolved for the two
   * evidence secrets, probed in the Worker/Functions runtime where those secrets bind (never a
   * build-time flag: the keys are absent at build and in CI). `search`: `'tavily'` when
   * `TAVILY_API_KEY` is present (real web discovery) else `'stub'` (simulated placeholder
   * sources); `model`: `'llm'` when `ANTHROPIC_API_KEY` is present (real record extraction +
   * source rating) else `'stub'`. Surfaced so the app can prove, at runtime, whether discovered
   * "evidence" is real — a `stub` here means the figures are simulation and must never be shown
   * as real evidence. Optional so an older api that predates this field still parses.
   */
  evidence: z.object({ search: z.enum(['tavily', 'stub']), model: z.enum(['llm', 'stub']) }).optional(),
});
export type HealthData = z.infer<typeof HealthDataSchema>;

/** Full `GET /health` response envelope. */
export const HealthResponseSchema = apiEnvelope(HealthDataSchema);
export type HealthResponse = z.infer<typeof HealthResponseSchema>;
